import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import moment from 'moment';
import _ from 'lodash';

import withChartExport from '../../../components/ChartExporting';
import dateUtils from '../../../util/dates';
import dataUtils from '../../../util/dataUtils';
import { isWhite, PERCENTILE_LINE_COLORS, PERCENTILE_LABEL_COLORS } from '../../../util/colors';
import { escapeHTML } from '../../../util/text';

import highchartRegression from '../../../util/highcharts-regression';
import highchartFactory from '../../../util/highcharts';
import chartUtils from '../../../util/chartUtils';
import { colorMap } from '../../../util/colorMap';
import { WALLBOARD_TEXT_SIZE } from '../../../util/wallboardFrame';
import { formatWorkDuration } from '../../../util/dates';
const highchartUtils = highchartFactory(Highcharts);
const DEFAULT_DOT_FILL = chartUtils.withOpacity(colorMap('default'), 0.8);

const chartConfig = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'scatter',
    zoomType: 'xy',
    spacingBottom: 10,
    spacingTop: 40,
    marginRight: 84,
    marginLeft: 70,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  time: {
    useUTC: false
  },
  xAxis: {
    type: 'datetime',
    lineColor: chartUtils.TREND_AXIS_LINE_COLOR,
    tickColor: chartUtils.TREND_AXIS_LINE_COLOR,
    labels: {
      style: {
        color: chartUtils.TREND_LABEL_COLOR,
        fontSize: '12px'
      }
    },
    tickInterval: 24 * 3600 * 1000,
    tickmarkPlacement: 'on',
    title: {
      enabled: false
    }
  },
  yAxis: {
    min: 0,
    opposite: false,
    allowDecimals: false,
    gridLineColor: chartUtils.TREND_GRID_COLOR,
    gridLineWidth: 1,
    title: {
      text: null
    },
    labels: {
      style: {
        color: chartUtils.TREND_LABEL_COLOR,
        fontSize: '12px',
        whiteSpace: 'nowrap'
      }
    },
    plotLines: []
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      stickyTracking: false,
      cursor: 'pointer',
      point: {
        events: {}
      }
    },
    scatter: {
      marker: {
        symbol: 'circle',
        radius: 7,
        lineWidth: 1,
        lineColor: '#FFFFFF',
        states: {
          hover: chartUtils.hoverMarker(),
          select: chartUtils.selectedMarker(10)
        }
      },
      states: {
        hover: {
          marker: {
            enabled: false
          }
        }
      }
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0,
    formatter: function() {}
  },
  series: []
};

export class CTSPChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      config: {}
    };
  }

  static defaultProps = {
    onPointClick: function() {}
  };

  calculatePercentiles = data => {
    const lines = [],
      ticks = [];
    const labelSize = this.props.wallmode ? { fontSize: WALLBOARD_TEXT_SIZE } : highchartUtils.getFontSizeConfig('percentile');

    for (let i = this.props.percentiles.length - 1; i >= 0; i--) {
      const pObj = _.find(data.percentiles, item => item.name == this.props.percentiles[i]);
      if (!pObj) {
        continue;
      }

      const text = `${pObj.name}%`;
      const value = chartUtils.daysInUnit(pObj.value, this.props.timeUnit);

      // above the grid (a line on a grid value would hide under the grid line), below the dots
      lines.push({
        color: PERCENTILE_LINE_COLORS[pObj.name],
        dashStyle: 'Dash',
        width: 1,
        zIndex: 2,
        value,
        label: {
          align: 'right',
          textAlign: 'right',
          className: 'highcharts-plot-line-label-right',
          text,
          x: 80,
          y: 4,
          style: {
            ...labelSize,
            color: PERCENTILE_LABEL_COLORS[pObj.name]
          }
        }
      });

      if (_.findIndex(ticks, { value }) === -1) {
        ticks.push({
          dashStyle: 'line',
          color: 'transparent',
          width: 4,
          zIndex: 2,
          value,
          events: highchartUtils.plotlinesTooltip(text)
        });
      }
    }

    const unit = { hour: 'h', day: 'd', week: 'w', month: 'mo' }[this.props.timeUnit] || '';
    const newLines = [];
    _(lines)
      .groupBy('value')
      .forEach(l => {
        const line = l[0];
        const labels = _.map(l, i => {
          return i.label.text;
        });
        line.label.text = `${labels.join(', ')} · ${line.value}${unit}`;
        newLines.push(line);
      });

    chartUtils.fitRightLineLabels(this.chart, newLines, { ...labelSize, fontWeight: 500 }, 84);
    return newLines.concat(ticks);
  };

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);
    highchartRegression(Highcharts);

    const that = this;
    chartConfig.tooltip.formatter = function() {
      return that.tooltipFormatter(that, this);
    };

    chartConfig.plotOptions.series.point.events.click = function() {
      that.onPointClick(that, this);
    };
    chartConfig.chart.events.render = function() {
      that.drawSelectionHalo();
    };

    chartConfig.exporting = this.props.chartExporting;

    this.chart = new Highcharts['Chart'](`${this.props.id}-details`, chartConfig);

    this.updateTimeLabelsFormat(this.props.dateFormat);

    if (this.props.data.series) {
      this.updateChart();
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isPointSelected && !this.props.isPointSelected && this.chart) {
      this.chart.getSelectedPoints().forEach(point => point.select(false, true));
      this.drawSelectionHalo();
    }

    if (prevProps.data != this.props.data) {
      this.updateChart(prevProps.data);
    }

    if (prevProps.percentiles != this.props.percentiles) {
      this.chart.update({
        yAxis: {
          plotLines: this.calculatePercentiles(this.props.data)
        }
      });
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateTimeLabelsFormat(this.props.dateFormat);
    }

    if (prevProps.timeUnit != this.props.timeUnit) {
      this.chart.update({ yAxis: this.cycleTimeLabels() });
    }

    if (prevProps.showNavigator != this.props.showNavigator) {
      this.chart.reflow();
    }

    if (prevProps.trendLine != this.props.trendLine) {
      this.updateRegression();
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename,
            chartOptions: {
              title: {
                text: 'Cycle Time Scatterplot',
                style: {
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              },
            }
          }
        },
        false
      );
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  drawSelectionHalo() {
    this.selectionHalo = chartUtils.drawSelectionHalo(this.chart, this.selectionHalo);
  }

  defaultDots(series) {
    return _.map(series, serie => (serie.color === colorMap('default') ? { ...serie, marker: { fillColor: DEFAULT_DOT_FILL, ...serie.marker } } : serie));
  }

  updateChart(oldData = {}) {
    this.chart.xAxis[0].setExtremes(null, null);

    const diff = dataUtils.getDiff(this.defaultDots(oldData.series), this.defaultDots(this.props.data.series), 'data');
    highchartUtils.updateSeries(diff, this.chart);
    const xExtremes = this.getExtremes();
    this.chart.update(
      {
        xAxis: {
          tickInterval: xExtremes.days * 24 * 3600 * 1000
        },
        yAxis: {
          ...this.cycleTimeLabels(),
          plotLines: this.calculatePercentiles(this.props.data)
        }
      },
      false
    );

    this.setExtremes(xExtremes);
    this.chart.redraw();
    this.updateRegression();
  }


  // the cycle time's unit sits in the labels: "10 days", "1 week"
  cycleTimeLabels() {
    const timeUnit = this.props.timeUnit;
    return {
      labels: {
        formatter: function() {
          return chartUtils.withUnit(this.value, timeUnit);
        }
      }
    };
  }
  updateRegression() {
    const trend = _.find(this.chart.series, { name: 'trend' });
    if (trend) {
      this.chart.series[trend.index].remove(true);
    }
    if (this.props.trendLine) {
      const data = _([...this.props.data.series])
        .flatMap('data')
        .sortBy('x')
        .value();

      this.chart.addSeries(
        {
          data,
          visible: false,
          regression: true,
          regressionSettings: {
            name: 'trend',
            index: 0,
            useAllSeries: true,
            type: 'polynomial',
            order: 6,
            color: 'rgb(87, 181, 70)',
            lineWidth: 3,
            dashStyle: 'LongDashDot'
          }
        },
        true
      );
    }
  }

  updateTimeLabelsFormat(dateFormat) {
    let config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.chart.update(config);
  }

  updateColors(colored) {
    if (colored) {
      const updateSeriesColor = (chart, serie) => {
        const index = _.findIndex(chart.series, {
          name: serie.name,
          color: serie.color
        });
        if (!!chart.series[index]) {
          chart.series[index].update({
            color: serie.color
          });
        }
      };

      for (let i = 0; i < this.props.data.series.length; i++) {
        const serie = this.props.data.series[i];
        updateSeriesColor(this.chart, serie);
      }
    } else {
      for (let i = 0; i < this.chart.series.length; i++) {
        this.chart.series[i].update({
          color: 'rgb(124,181,236)'
        });
      }
    }
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('ctsp');
    this.chart.update({
      ...newConfig.chart,
      // the TV's larger axis labels take the room they need
      chart: { marginLeft: null }
    });
  }

  getExtremes() {
    let minVal, maxVal;

    const sorted = _([...this.props.data.series])
      .flatMap('data')
      .orderBy('x')
      .map('x');
    minVal = sorted.first();
    maxVal = sorted.last();

    if (moment(minVal).isSameOrAfter(this.props.dates.startDate)) {
      minVal = this.props.dates.startDate;
    }
    if (moment(maxVal).isSameOrAfter(this.props.dates.endDate)) {
      maxVal = this.props.dates.endDate;
    }

    switch (this.props.timeUnit) {
      case 'week': {
        minVal = +moment(minVal).startOf('week');
        maxVal = +moment(new Date(maxVal).endOf('week')).add(2, 'days');
        break;
      }
      case 'month': {
        minVal = +moment(minVal).date(1);
        maxVal = +moment(maxVal).date(30);
        break;
      }
      default: {
        minVal = +minVal;
        maxVal = +maxVal;
        break;
      }
    }

    return {
      min: minVal,
      max: maxVal,
      days:  Math.max(moment(maxVal).diff(moment(minVal), 'days') / 10, 1)
    };
  }

  setExtremes(xExtremesObject, yExtremesObject) {
    if (xExtremesObject) {
      this.chart.xAxis[0].setExtremes(xExtremesObject.min, xExtremesObject.max);
    }
    if (yExtremesObject) {
      this.chart.yAxis[0].setExtremes(yExtremesObject.min, yExtremesObject.max);
    } else {
      this.chart.yAxis[0].setExtremes(null, null);
    }
  }

  getPeriodTitle(date) {
    return chartUtils.periodTitle(date, this.props.timeUnit, this.props.dateFormat);
  }

  onPointClick(comp, ev) {
    chartUtils.selectOnly(comp.chart, ev);
    comp.drawSelectionHalo();

    const points = [];
    const date = comp.getPeriodTitle(ev.x);
    const duration = formatWorkDuration(
      ev.cycleTimeInMinutes,
      comp.props.excludeWeekendOpts,
      comp.props.timePrecision,
      comp.props.useWorkingHoursInCycleTimeDays
    );

    for (let s = 0; s < comp.chart.series.length; s++) {
      for (let i = 0; i < comp.chart.series[s].data.length; i++) {
        const point = comp.chart.series[s].data[i];
        if (moment(point.x).isSame(ev.x, 'hour') && point.y == ev.y && point.card) {
          points.push({
            card: point.card,
            details: point.details,
            color: point.color
          });
        }
      }
    }

    comp.props.onPointClick({
      date,
      value: duration,
      points
    });
  }

  tooltipFormatter(comp, ev) {
    if (ev.series.name === 'trend') {
      return '<div></div>';
    }

    if (ev.key == 'Plotline') {
      return `${ev.point.text}: ${ev.y} ${comp.props.timeUnit}s`;
    }

    let date = comp.getPeriodTitle(ev.x);
    const duration = formatWorkDuration(
      ev.point.cycleTimeInMinutes,
      comp.props.excludeWeekendOpts,
      comp.props.timePrecision,
      comp.props.useWorkingHoursInCycleTimeDays
    );
    let tooltip = `<p class="tooltip-label-title-size" style="margin: 8px 8px 0;"><small style="color: #545D66; font-size: 12px; font-weight: 400">${_.upperFirst(comp.props.selectedDate)} date ·</small> ${date}</p> <p class="tooltip-label-size" style="margin: 8px 10px 0;">Cycle time: ${duration}</p>`;

    for (let s = 0; s < comp.chart.series.length; s++) {
      for (let i = 0; i < comp.chart.series[s].data.length; i++) {
        let point = comp.chart.series[s].data[i];
        if (point.card && moment(point.x).isSame(ev.x, 'hour') && point.y == ev.y) {
          const isBgWhite = isWhite(point.color) ? 'border: 1px solid #00408026;' : '';
          tooltip += `<div class="tooltip-list-item"><div class="tooltip-circle-dot" style="${isBgWhite} background: ${point.color}"></div>${escapeHTML(point.card.name)}</div>`;
        }
      }
    }

    tooltip += `<p class="tooltip-info-txt">Click on the dot to see the details</p>`;

    return tooltip;
  }

  render() {
    return (
      <div className="chart_content chart_tooltip_style">
        <div id={`${this.props.id}-details`} className={'chart_details chart_details-full'}></div>
      </div>
    );
  }
}

CTSPChart.propTypes = {
  onPointClick: PropTypes.func
};

const mapStateToProps = state => {
  return {
    dates: state.filters.dates,
    dateFormat: state.filters.dateFormat,
    timePrecision: state.filters.timePrecision,
    percentiles: state.filters.ctsp.selectedPercentiles,
    timeUnit: state.filters.ctsp.timeUnit,
    selectedDate: state.filters.ctsp.selectedDate,
    trendLine: state.filters.ctsp.trendLine,
    wallmode: state.app.wallmode,
    excludeWeekendOpts: {
      type: state.filters.excludeWeekends,
      workStart: state.filters.workStart,
      workEnd: state.filters.workEnd,
      holidays: state.filters.holidays
    },
    useWorkingHoursInCycleTimeDays: state.auth.user.config && state.auth.user.config.useWorkingHoursInCycleTimeDays
  };
};

export default withChartExport(connect(mapStateToProps)(CTSPChart));
